import React, { useState } from 'react';
import { Button, FormGroup } from 'reactstrap';
import csv from "csvtojson";
import {NotificationManager} from "react-notifications";
import moment from 'moment';

const OrderImport = ({ client, whiteLabelPartners, installers, ...props }) => {
  const hiddenFileInput = React.useRef(null);

  const onClick = () => {
    hiddenFileInput.current.click(); 
  };

  const verifyDate = (dateString) => {
    const isValidDate = moment(dateString, "DD/MM/YYYY", true).isValid();
    if (isValidDate){
      return true
    } else {
      return false;
    }
  };

  const fetchInstallationType = async () => {
    const response = await client.get('installation_types');
    const enumValues = response.data.flatMap(item => [item.installation_type, item.display_name]);
    return enumValues;
  };

  const fetchDeviceType = async () => {
    const response = await client.get('devices');
    const enumValues = response.data.flatMap(item => [item.device_name, item.display_name]);
    return enumValues;
  };

  const fetchCountryList = async () => {
    const response = await client.get('countries');
    const countryValues = response.data.flatMap(item => item.name);
    return countryValues;
  };

  const fetchJobType = async () => {
    const response = await client.get('job_types');
    const filteredResponse = response.data.filter(item => item.text !== 'Miscellaneous');
    const enumValues = filteredResponse.map(item => item.text);
    return enumValues;
  };

  const verifyInstallationType = (installationTypeList, enumValues) => {
      const installationTypes = installationTypeList.split(',')
      const invalidInstallationTypes = installationTypes.filter(
        installationType => !enumValues.includes(installationType.trim()));
      return invalidInstallationTypes.length === 0;
    };

  const verifyDeviceType = (deviceType, enumValues) => {
    return enumValues.includes(deviceType.trim());
  };
  
  const verifyJobType = (jobType, enumValues) => {
    return enumValues.includes(jobType.trim());
  }

  const verifyImei = (imei) => {
    const imeiFormat = /^[a-zA-Z0-9]{1,30}$/;
    return imei.match(imeiFormat);
  };

  const handleFile = (file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      const binaryString = e.target.result;
      csv().fromString(binaryString).then(function(jsonArrayObj) {
        let json = {};
        const errorGroups = {};
        const errors = {};
        const headers = Object.keys(jsonArrayObj[0]);
        jsonArrayObj.forEach(async (row, rowNumber) => {
          if (rowNumber === 0) {
            const requiredFields = [
              "customerName",
              'contactName',
              'addressLine1',
              'townOrCity',
              'country',
              'contactTelNo',
              'contactEmailAddress',
              'customerEmailAddress',
              'custNo',
              'jobType',
              'surrogateId'
            ];

            const missingFields = requiredFields.filter((field) => !headers.includes(field) || !row[field] || row[field].trim() === "");
            if (missingFields.length > 0) {
              errors.requiredFields = `Missing required fields on row ${rowNumber + 2}: ${missingFields.join(', ')}`;
            }

            if (row["jobType"]) {
              const enumValues = await fetchJobType();
              const jobType = row["jobType"];
              const isValid = verifyJobType(jobType, enumValues);
              if(!isValid) {
                errors.jobType = `Invalid jobType: ${row["jobType"]} on row ${rowNumber + 2}. Valid values are: ${enumValues.join(', ')}`;
              }
            }

            let whiteLabelPartner;
            if (row["whiteLabelPartner"]) {
              whiteLabelPartner = whiteLabelPartners.find(x => x.name === row["whiteLabelPartner"]).id;
            }

            let installer;
            if (row["installer"]) {
              installer = installers.find(x => x.name === row["installer"]).id;
            }

            // Customer details
            json["customerName"] = row["customerName"];
            json["contactName"] = row["contactName"];
            json["addressLine1"] = row["addressLine1"];
            json["addressLine2"] = row["addressLine2"];
            json["townOrCity"] = row["townOrCity"];
            json["county"] = row["county"];
            json["postCode"] = row["postCode"];
            if (row["country"]) {
              const countryList = await fetchCountryList();
              if(countryList.includes(row["country"])) {
                json["country"] = row["country"];
              } else {
                errors.country = `Invalid country name: ${row["country"]} on row ${rowNumber + 2}.`;
              }
            }
            json["contactTelNo"] = row["contactTelNo"];
            json["contactEmailAddress"] = row["contactEmailAddress"];

            // Order details
            json["customerEmailAddress"] = row["customerEmailAddress"];
            json["custNo"] = row["custNo"];
            json["jobType"] = row["jobType"];
            json["surrogateId"] = row["surrogateId"];
            if (row["serviceDeskTicket"]) {
              json["serviceDeskTicket"] = row["serviceDeskTicket"];
            }
            json["installer"] = installer;
            json["whiteLabelPartner"] = whiteLabelPartner;
            json["notes"] = row["notes"];
            json["issuerId"] = row["issuerId"];
            json["premiumInstallation"] = row["premiumInstallation"];
            if (row.dispatchAddress && row.dispatchAddress.trim() !== "") {
              json["dispatchAddress"] = row["dispatchAddress"];
            }
            if (row.salesAgentEmail && row.salesAgentEmail.trim() !== "") {
              json["salesAgentEmail"] = row["salesAgentEmail"];
            }
            if (row.controlNotes && row.controlNotes.trim() !== "") {
              json["controlNotes"] = row["controlNotes"];
            }
            if (row.kitDispatchedDate && row.kitDispatchedDate.trim() !== "") {
                if (!verifyDate(row['kitDispatchedDate'])) {
                  errors.kitDispatchedDate = `Invalid date format for kitDispatchedDate on row ${rowNumber + 2}, must be valid and in format of DD/MM/YYYY`;
                } else {
                  json['kitDispatchedDate'] = moment(row['kitDispatchedDate'], 'DD/MM/YYYY').format('YYYY-MM-DD');
                }
            }
            if (row.courierTrackingNumber && row.courierTrackingNumber.trim() !== "") {
              json["courierTrackingNumber"] = row["courierTrackingNumber"];
            }
            if (row.projectOrder && row.projectOrder.trim() !== "") {
              json["projectOrder"] = row["projectOrder"];
            }
            if (row.preferredInstallDates && row.preferredInstallDates.trim() !== "") {
                let prefDates = []
                const installDates = row["preferredInstallDates"].split(',').map(date => date.trim());

                if (installDates.length>3){
                  errors.preferredInstallDates = `Maximum 3 dates are allowed on row ${rowNumber + 2}`;
                } else {
                  installDates.slice(0,3).forEach(date => {
                    if (!verifyDate(date)) {
                      errors.preferredInstallDates = `Invalid date format for preferredInstallDates on row ${rowNumber + 2}, must be valid and in format of DD/MM/YYYY`;
                    } else {
                      prefDates.push(moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD'));
                    }
                  })
                }
                json["preferredInstallDates"] = prefDates;
            }

            json["vehicleList"] = [];
          }

          let job = {
            "appointment_date": row["appointment_date"],
            "job_completed_at": row["job_completed_at"],
            "imei": row["imei"],
            "serial_number": row["serial_number"],
            "deviceType": row["deviceType"],
            "make": row["make"],
            "vehicle_model": row["vehicle_model"],
            "yearOfManufacture": row["yearOfManufacture"],
            "registration": row["registration"],
          };

          if (row.deviceMakeModel && row.deviceMakeModel.trim() !== "") {
            job.deviceMakeModel = row["deviceMakeModel"]
          }
          if (row.supplementaryItemTypes && row.supplementaryItemTypes.trim() !== "") {
            job.supplementaryItemTypes = row["supplementaryItemTypes"]
          }

          if (row["installationTypeList"]) {
            const enumValues = await fetchInstallationType();
            const installationTypeList = row["installationTypeList"];
            const installationTypes = installationTypeList
            .split(',')
            .map(installationType => installationType.trim());
            const isValid = verifyInstallationType(installationTypeList, enumValues);
              if (isValid) {
                job["installationTypeList"] = installationTypes;
              } else {
                errors.installationTypeList = `Invalid installationTypeList: ${row["installationTypeList"]} on row ${rowNumber + 2}`;
              }
          }

          if (row["deviceType"]) {
            const enumValues = await fetchDeviceType();
            const deviceType = row["deviceType"];
            const isValid = verifyDeviceType(deviceType, enumValues);
              if (isValid) {
                job["deviceType"] = deviceType;
              } else {
                errors.deviceType = `Invalid deviceType: ${deviceType} on row ${rowNumber + 2}, valid values are: ${enumValues.join(', ')}`;
              }
          }

          if (row["additionalEquipment"]) {
            job["additionalEquipment"] = row["additionalEquipment"].split(',');
          }
          if (row["fuelTypeCode"]) {
            job["fuelTypeCode"] = row["fuelTypeCode"];
          }
          if (row["voltage"]) {
            if(row["voltage"] === "12v" || row["voltage"] === "24v")
            {
              job["voltage"] = row["voltage"];
            } else if (row["voltage"] === "1" || row["voltage"] === "2") {
              job["voltage"] = row["voltage"];
            } else if (row["voltage"] === "12" || row["voltage"] === "24") {
              job["voltage"] = row["voltage"];
            } else {
              errors.voltage = `Invalid voltage: ${row["voltage"]} on row ${rowNumber + 2}, it must be one of these (1,12,12v,2,24,24v)`;
            }
          } else {
            job["voltage"] = "12v"
          }

          const requiredFieldsJob = [
            'installationTypeList',
            'make',
            'imei',
            'vehicle_model',
            'deviceType',
            'registration'
          ]

          const missingFieldsJob = requiredFieldsJob.filter((field) => !headers.includes(field) || !row[field] || row[field].trim() === "");
          if (missingFieldsJob.length > 0) {
            errors.requiredFieldsJob = `Missing required fields on row ${rowNumber + 2}: ${missingFieldsJob.join(', ')}`;
          }

          const numberFields = 
          ['surrogateId', 
          'custNo', 
          'contactTelNo', 
          'yearOfManufacture'
          ];
          //Have left IMEI out due to some having a mixture of letters and characters (Badai)
          const nanFields = numberFields.filter((field) => row[field] && (row[field]).match(/^[0-9]+$/) === null);
          if (nanFields.length > 0) {
            errors.invalidNumber = `Fields must be a number on row ${rowNumber + 2}: ${nanFields.join(', ')}`;
          }

          if (row['appointment_date']) {
            if (!verifyDate(row['appointment_date'])) {
              errors.appointmentDate = `Invalid date format for appointment_date on row ${rowNumber + 2}, must be valid and in format of DD/MM/YYYY`;
            } else {
              job.appointment_date = moment(row['appointment_date'], 'DD/MM/YYYY').format('YYYY-MM-DD');
            }
          }
    
          if (row['job_completed_at']) {
            if (!verifyDate(row['job_completed_at'])) {
              errors.completedDate = `Invalid date format for job_completed_at on row ${rowNumber + 2}, must be valid and in format of DD/MM/YYYY`;
            } else {
              job.job_completed_at = moment(row['job_completed_at'], 'DD/MM/YYYY').format('YYYY-MM-DD');
            }
          }

          if (row['imei']) {
            if (!verifyImei(row['imei'])) {
              errors.imei = `Invalid IMEI format for imei ${row['imei']} on row ${rowNumber + 2}, must be <= 30 digits`;
            }
          }

          json["vehicleList"].push(job);

          Object.keys(errors).forEach((errorType) => {
            if (!errorGroups[errorType]) {
              errorGroups[errorType] = [];
            }
            if (!errorGroups[errorType].includes(errors[errorType])) {
              errorGroups[errorType].push(errors[errorType]);
            }
          });
  
          Object.keys(errorGroups).forEach((errorType) => {
            if (errorGroups[errorType].length > 0) {
              NotificationManager.error(errorGroups[errorType].join('\n'), errorType, 0,
                () => {
                  // Close all notifications if one notification is clicked
                  NotificationManager.listNotify.forEach(n => NotificationManager.remove({ id: n.id }));
                },
              );
            }
          });

          if (rowNumber === 0) {
          if (Object.keys(errors).length === 0) {
            client.post(`import_order`, json).tap(() => {
              NotificationManager.success('CSV successfully imported');
            }).catch((error) => {
              /* Old error handling returned the full response data, returning an object and causing it to crash; 
                message is better than returning the whole response as a string */
              NotificationManager.error((error.response.data.message), 0, 0);
            });
          };
        };
        });
      });
    };
    reader.readAsBinaryString(file);
  };

  const onChange = (event) => {
    const fileUploaded = event.target.files[0];
    handleFile(fileUploaded);
    event.target.value = '';
  };

  return (
    <FormGroup {...props} className="job-status-template clearfix">
      <Button className="pull-right" color="primary" onClick={onClick}>
        Import CSV
      </Button>
      <input
        type="file"
        ref={hiddenFileInput}
        onChange={onChange}
        style={{display:'none'}}
        accept=".csv"
      />
    </FormGroup>
  );
};

export default OrderImport;


// WEBPACK FOOTER //
// ./src/components/common/OrderImport/OrderImport.jsx